import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EditableTitle } from '@/components/ui/editable-heading';
import { MAX_BLOCK_NAME_LENGTH } from '@/lib/block-naming';

interface BlockNameFieldProps {
  /** Teacher-facing block name (see block-naming.ts) — editable inline whenever
   * onBlockNameChange is provided; shown as plain text otherwise. */
  blockName: string;
  /** Static block-type name — fallback label only if blockName is ever empty. */
  titleLabel: string;
  onBlockNameChange?: (name: string) => void;
  /** Rejects a candidate rename with an inline error instead of committing it. */
  isBlockNameTaken?: (candidate: string) => boolean;
}

/** Shared editable-title-bar field, used by InputBlockFrame and every display block's own
 * (hand-rolled, non-InputBlockFrame) editing header — same permanent-background affordance as
 * the task title itself, so it reads as editable at a glance rather than only on hover. */
export function BlockNameField({ blockName, titleLabel, onBlockNameChange, isBlockNameTaken }: BlockNameFieldProps) {
  const { t } = useTranslation();
  const [nameError, setNameError] = useState<string | null>(null);

  // Reached only with a trimmed name that differs from the current one — EditableTitle owns the
  // revert-on-empty and the no-op-on-unchanged. What is left is the part only this caller knows.
  function commitName(name: string) {
    if (isBlockNameTaken?.(name)) {
      setNameError(t('task.blockName.duplicate'));
      return;
    }
    setNameError(null);
    onBlockNameChange?.(name);
  }

  if (!onBlockNameChange) {
    return <span className='flex-1 truncate'>{blockName || titleLabel}</span>;
  }

  return (
    <div className='flex min-w-0 flex-1 items-center gap-1'>
      <EditableTitle
        value={blockName}
        // A hard stop while typing, rather than a rejection after it. The limit is a prompt to
        // rethink the name mid-word; told at commit, the thought is already finished.
        maxLength={MAX_BLOCK_NAME_LENGTH}
        onCommit={commitName}
        onEdit={() => setNameError(null)}
        error={nameError}
        ariaLabel={t('task.blockName.label')}
        className='flex-1' />
      {nameError && (
        <span title={nameError} className='flex h-4 w-4 shrink-0 items-center justify-center text-ws-error-fg'>
          <AlertTriangle aria-hidden='true' className='h-3.5 w-3.5' />
        </span>
      )}
    </div>
  );
}
